import { inject } from '@angular/core';
import { keys } from '@sama/micro-dash';
import { loadAsync } from 'jszip';
import { EventTrackingValue } from '../../team-apis/core/event-tracking';
import {
  AssetLoadError,
  UrlRequestService,
} from '../misc-services/resources/url-request.service';
import { SensorFrame2D } from '../state/sensor-renderer-state';
import { ObjectWith } from '../types/misc';
import { BlobService } from './blob.service';

export abstract class UnzipService {
  protected blobService = inject(BlobService);

  private urlRequestService = inject(UrlRequestService);

  async fetchAndUnzip(url: string) {
    const asset = await this.urlRequestService.downloadResource(url);
    const unzippedBlobUrls = await this.unzip(asset, url);
    const { sensorFrames, eventTrackingValue } =
      this.collateBlobUrls(unzippedBlobUrls);
    return {
      sensorFrames,
      eventTrackingValue: {
        ...eventTrackingValue,
        file_bytes: asset.byteLength,
      },
    };
  }

  // public so we can stub it in tests
  async unzip(asset: ArrayBuffer, url: string) {
    return unzip(asset, url, this.blobService);
  }

  protected abstract collateBlobUrls(unzippedBlobUrls: ObjectWith<string>): {
    sensorFrames: SensorFrame2D[];
    eventTrackingValue: EventTrackingValue;
  };
}

export async function unzip(
  asset: ArrayBuffer,
  url: string,
  // Pick used to make it compatible with MockBlobStore
  blobService: Pick<BlobService, 'saveToBlobStore'>,
) {
  try {
    const zip = await loadAsync(asset);

    const unzippedBlobUrls: ObjectWith<string> = {};
    for (const path of keys(zip.files)) {
      const content = await zip.files[path].async('arraybuffer');
      unzippedBlobUrls[path] = blobService.saveToBlobStore(
        content,
        `${url}/${path}`,
      );
    }
    return unzippedBlobUrls;
  } catch (error) {
    throw new AssetLoadError(
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      determineMessageForJszipError((error as Error) || ''),
    );
  }
}

function determineMessageForJszipError(error: Error) {
  if (/is this a zip file/.test(error.message)) {
    return 'This is not a valid zip file';
  } else if (/Encrypted zip are not supported/.test(error.message)) {
    return 'The zip file is password protected';
  } else if (/Corrupted zip or bug/.test(error.message)) {
    return 'The zip file is corrupted';
  } else {
    return 'There was an error loading the zip file';
  }
}
